import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import mqtt, { MqttClient } from 'mqtt';
import { BehaviorSubject, Subject } from 'rxjs';

export type MqttConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

@Injectable({
  providedIn: 'root'
})
export class MqttService {
  private client?: MqttClient;
  private messageSubject = new Subject<{ topic: string; message: string }>();
  public messages$ = this.messageSubject.asObservable();

  private connectionStatusSubject = new BehaviorSubject<MqttConnectionState>('disconnected');
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  private readonly brokerUrl = 'wss://mqtt.adhivasindo.com:443/mqtt';
  private readonly options = {
    clientId: window.crypto.randomUUID(),
    username: environment.mqtt.username,
    password: environment.mqtt.password,
    clean: true,
    reconnectPeriod: 2000, // auto reconnect setiap 2 detik jika terputus
    connectTimeout: 10_000, // 10 detik timeout
  };

  constructor() {
    // tidak langsung connect agar bisa dikontrol via login/logout
  }

  /** 🔌 Connect ke broker MQTT */
  public connect(): void {
    if (this.client && this.client.connected) {
      console.log('⚡ MQTT already connected');
      this.connectionStatusSubject.next('connected');
      return;
    }

    console.log('🔗 Connecting to MQTT...');
    this.connectionStatusSubject.next('connecting');
    this.client = mqtt.connect(this.brokerUrl, this.options);

    this.client.on('connect', () => {
      console.log('✅ MQTT connected');
      this.connectionStatusSubject.next('connected');
    });

    this.client.on('reconnect', () => {
      console.log('♻️ MQTT reconnecting...');
      this.connectionStatusSubject.next('reconnecting');
    });

    this.client.on('error', (err) => {
      console.error('❌ MQTT error:', err);
      this.connectionStatusSubject.next('error');
    });

    this.client.on('close', () => {
      console.warn('🚪 MQTT connection closed');
      this.connectionStatusSubject.next('disconnected');
    });

    this.client.on('message', (topic, payload) => {
      const message = payload.toString();
      this.messageSubject.next({ topic, message });
    });
  }

  /** 📩 Subscribe ke topik tertentu */
  public subscribe(topic: string): void {
    if (!this.client) {
      console.warn('MQTT client belum terhubung, menunggu connect...');
      this.connect();
    }

    const doSubscribe = () => {
      this.client?.subscribe(topic, (err) => {
        if (err) console.error('Subscribe error:', err);
        else console.log(`📡 Subscribed to: ${topic}`);
      });
    };

    if (this.client?.connected) doSubscribe();
    else this.client?.once('connect', doSubscribe);
  }

  /** 🔕 Unsubscribe dari topik tertentu */
  public unsubscribe(topic: string): void {
    if (this.client?.connected) {
      this.client.unsubscribe(topic, (err) => {
        if (err) console.error('Unsubscribe error:', err);
        else console.log(`🔕 Unsubscribed from: ${topic}`);
      });
    }
  }

  /** 📤 Kirim pesan ke topik tertentu */
  public publish(topic: string, message: string): void {
    if (this.client?.connected) {
      this.client.publish(topic, message);
      console.log(`📨 Published to ${topic}:`, message);
    } else {
      console.warn('⚠️ MQTT belum terhubung, tidak bisa publish');
    }
  }

  /** 🔕 Disconnect dari broker */
  public disconnect(): void {
    if (this.client) {
      this.client.end(true, () => console.log('📴 MQTT disconnected'));
      this.client = undefined;
      this.connectionStatusSubject.next('disconnected');
    }
  }
}

